'use client';

import { Dialog } from '@base-ui/react/dialog';
import { css } from '@repo/pandacss/css';
import { AnalyticsFeatureViewed } from '@repo/platform-analytics/AnalyticsFeatureViewed';
import { getOrCreateId } from '@repo/platform-utils/static-ids';
import { useMemo, useRef } from 'react';
import type { BranchId } from '@/shared/config/funnel/constants';
import { PICKER_ACTION_NAME, PICKER_CLOSE_ACTION_INPUT, PICKER_ENTITY_NAME } from '../../../config/exploreOtherCardTypes';
import { useFunnelFlattenedConfig, useFunnelStepId } from '../../../model/Context/hooks';
import type { CardTypeOption } from '../../../model/exploreOtherCardTypes';
import { useFunnelContextChanges } from '../../../model/in-funnel/context-changes';
import { applyInterruptOutcome } from '../../../model/interrupts/applyOutcome';
import { useBranchedPageAction } from '../../../model/useBranchedPageAction';
import { useFunnelNavigation } from '../../../model/useFunnelNavigation';
import { BranchOptions } from './BranchOptions';
import { Close } from './Close';
import { Title } from './Title';
type Props = {
  options: CardTypeOption[];
  currentBranchId: BranchId;
  onClose: () => void;
};
export function CardTypePicker({
  options,
  currentBranchId,
  onClose
}: Props) {
  const stepId = useFunnelStepId();
  const funnelFlattenedConfig = useFunnelFlattenedConfig();
  const {
    changeGoal
  } = useFunnelContextChanges();
  const {
    goToStep
  } = useFunnelNavigation();
  const activityIdRef = useRef('');
  const activityId = getOrCreateId(activityIdRef);
  const fireSelectAction = useBranchedPageAction({
    actionName: PICKER_ACTION_NAME,
    actionSection: PICKER_ENTITY_NAME,
    actionType: 'select',
    parentActivityId: activityId
  });
  const additionalAttributes = useMemo(() => ({
    page_branch_name: currentBranchId
  }), [currentBranchId]);
  const handleSelect = (branchId: BranchId) => {
    fireSelectAction({
      action_input: branchId
    });
    onClose();
    if (branchId === currentBranchId) return;
    applyInterruptOutcome({
      kind: 'reset-branch',
      branchId
    }, {
      changeGoal,
      goToStep,
      funnelFlattenedConfig
    });
  };
  return <Dialog.Root open onOpenChange={open => {
    if (!open) {
      fireSelectAction({
        action_input: PICKER_CLOSE_ACTION_INPUT
      });
      onClose();
    }
  }}>
      <Dialog.Portal>
        <Dialog.Backdrop className={css({
        position: 'fixed',
        inset: '[0]',
        bg: '[rgba(0,0,0,0.5)]',
        zIndex: '[1000]'
      })} />
        <Dialog.Viewport className={css({
        position: 'fixed',
        inset: '[0]',
        zIndex: '[1001]',
        display: 'flex',
        alignItems: {
          base: 'flex-end',
          md: 'center'
        },
        justifyContent: 'center'
      })}>
          <Dialog.Popup className={css({
          bg: 'common.white',
          width: {
            base: '[100%]',
            md: '[540px]'
          },
          maxHeight: '[90vh]',
          overflowY: 'auto',
          rounded: {
            base: '[16px 16px 0 0]',
            md: 'lg'
          },
          shadow: 'modal'
        })}>
            <AnalyticsFeatureViewed activityId={activityId} entityName={PICKER_ENTITY_NAME} entitySection={`${currentBranchId}_${stepId}`} additionalAttributes={additionalAttributes}>
              <div className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: {
                base: '3',
                md: '0'
              },
              p: {
                base: '3',
                md: '3.5'
              },
              pb: {
                base: '8',
                md: '3.5'
              }
            })}>
                <div className={css({
                display: 'flex',
                alignItems: 'flex-start',
                gap: '3',
                pb: {
                  md: '3.5'
                }
              })}>
                  <Title />
                  <Close />
                </div>
                <BranchOptions options={options} currentBranchId={currentBranchId} onSelect={handleSelect} />
              </div>
            </AnalyticsFeatureViewed>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>;
}